import React, { useContext, useEffect, useState } from 'react'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { Deck } from '@deck.gl/core'
import { HeatmapLayer } from '@deck.gl/aggregation-layers'
import { MapContext } from './mapContext'
import { i } from 'mathjs'
import chroma from 'chroma-js'
import { colorMaps } from '~/design'
import { mapRangeWithFixedBounds } from '~/prix/utils/math/mapRange'
import { WeightedLocation } from '../map/mapPosition'

interface HeatmapProps {
  positions: WeightedLocation[]
  dissipating?: boolean
  opacity?: number
  radius?: number
  gradient?: string[]
  resolution?: number
}

export default function Heatmap({
  positions,
  dissipating = true,
  opacity,
  radius,
  gradient = colorMaps.toHeatHeatmap,
  resolution = 8,
}: HeatmapProps) {
  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Heatmap must be used within a Map')
  }

  const { map, google, mapType, is3D } = mapContext

  useEffect(() => {
    if (!map || !google) {
      return
    }

    const defaultRadius = mapRangeWithFixedBounds(resolution, 5, 15, 100, 150)

    if (is3D) {
      const colorRange = gradient ? gradient.map(color => chroma(color).rgb()) : undefined

      type Item = {
        coordinates: [number, number]
        weight: number
      }
      const data: Array<Item> = positions.map(position => {
        return {
          coordinates: [position.location.lng, position.location.lat],
          weight: position.weight || 1,
        }
      })

      const layer = new HeatmapLayer<Item>({
        id: 'heatmap-layer',
        data,

        aggregation: 'SUM',
        getPosition: item => item.coordinates,
        getWeight: item => item.weight,
        radiusPixels: radius ?? defaultRadius,
        threshold: 0.4,
        colorRange,
      })

      const overlay = new GoogleMapsOverlay({
        layers: [layer],
      })

      overlay.setMap(map)

      return () => {
        overlay.setMap(null)
      }
    }

    const data = positions.map(position => ({
      location: new google.maps.LatLng(position.location.lat, position.location.lng),
      weight: position.weight ?? 1,
    }))

    const heatmap = new google.maps.visualization.HeatmapLayer({
      data,
      map,
      dissipating,
      opacity,
      radius: radius ?? defaultRadius * 0.8,
      gradient: gradient ? ['transparent', ...gradient] : undefined,
    })

    return () => {
      heatmap.setMap(null)
    }
  }, [map, google, positions, mapType, dissipating, opacity, radius, gradient, resolution])

  return null
}
